import {
  Callout,
  DATE_FORMAT,
  FileCard,
  type IconName,
  PanelSection,
  Tag,
  UploadButton,
  type UploadButtonProps,
} from '@dev-spendesk/grapes';
import { type ReactNode } from 'react';
import { Trans } from 'react-i18next';

import { useCompany } from 'modules/app/hooks/useCompany';
import { SpanishDPRCheckmarkFileCard } from 'modules/documentary-evidence/components/SpanishDPRCheckmark';
import { getSpanishDPRFromCompliances } from 'modules/documentary-evidence/utils/getSpanishDPRFromCompliances';
import { isTravelperkPayable } from 'modules/integrations/utils/travelperk';
import { useIsEditablePayable } from 'modules/payable/hooks';
import { DownloadEInvoiceXmlLink } from 'src/core/common/components/DownloadEInvoiceXmlLink/DownloadEInvoiceXmlLink';
import { PanelPreviewsSection } from 'src/core/common/components/Panel';
import {
  type I18nKey,
  type LocaleFormat,
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import { UserAvatar } from 'src/core/modules/users/components';
import { getFileTypeFromMime } from 'src/core/utils/files';

import { AutomaticallyCollectedReceipt } from '../../../../integrations/settings/components/AutomaticallyCollectedReceipt';
import type {
  DocumentViewable,
  Payable,
} from '../../containers/PayablePanelContainer';
import { useIsUkMileagePayable } from '../../models/payable';

export const ReceiptSection = ({
  payable,
  onDocumentClick,
  onDocumentUpload,
  isUploadingDocument,
}: {
  payable: Payable;
  onDocumentClick(document: DocumentViewable): void;
  onDocumentUpload?(files: File[] | null): void;
  isUploadingDocument?: boolean;
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { t, localeFormat } = useTranslation('global');

  const {
    id: payableId,
    documentaryEvidence,
    documentaryEvidenceCannotBeProvided,
  } = payable;

  const canUploadDocumentaryEvidence =
    useCanUploadDocumentaryEvidence(payable) && !!onDocumentUpload;

  const title = t('payables.panel.receipt');

  if (documentaryEvidenceCannotBeProvided?.reportMethod === 'manual') {
    return (
      <PanelSection title={title}>
        <Callout
          variant="warning"
          title={getLocalizedMissingStatus({
            id: '',
            fullname:
              (
                documentaryEvidenceCannotBeProvided as unknown as {
                  // TODO: this property doesnt seem to exist
                  reporterGivenName: string | undefined;
                }
              ).reporterGivenName ?? t('misc.unknown'),
          })}
        >
          {documentaryEvidenceCannotBeProvided.reason && (
            <blockquote className="mb-16 border-0 border-s-2 border-solid border-border-warning pl-4 body-m">
              {documentaryEvidenceCannotBeProvided.reason}
            </blockquote>
          )}
        </Callout>
      </PanelSection>
    );
  }

  if (documentaryEvidenceCannotBeProvided?.reportMethod === 'automatic') {
    return (
      <PanelPreviewsSection
        title={title}
        noPreviewContent={t('payables.panel.missingInvoiceIsRefundAutomatic')}
        previews={[]}
      />
    );
  }

  const attachments = (documentaryEvidence?.attachments ?? []).sort(
    (a, b) => (a.attachedAt?.getTime() ?? 0) - (b.attachedAt?.getTime() ?? 0),
  );

  if (documentaryEvidence?.type === 'declarationOfMissingInvoice') {
    const previews = attachments.map((attachment) => (
      <FileCard
        key={attachment.url}
        onClick={() => {
          onDocumentClick({ ...documentaryEvidence, ...attachment });
        }}
        title={
          getAttachmentPreviewText(attachment, t, localeFormat, true) as string
        }
        mimeType={attachment.mediaType}
      />
    ));

    return (
      <PanelPreviewsSection
        title={title}
        noPreviewContent={t('payables.panel.noReceipt')}
        previews={previews}
      />
    );
  }

  if (documentaryEvidence?.validity.isValid) {
    const previews = attachments.map((attachment) => {
      const { hasSpanishDPR, state: spanishDPRState } =
        getSpanishDPRFromCompliances(attachment.compliances);

      return (
        <div key={attachment.url} className="rounded-12 bg-background-secondary-default">
          <FileCard
            onClick={() => {
              onDocumentClick({
                ...documentaryEvidence,
                ...attachment,
              });
            }}
            title={
              getAttachmentPreviewText(attachment, t, localeFormat) as string
            }
            mimeType={attachment.mediaType}
          />
          {hasSpanishDPR && (
            <SpanishDPRCheckmarkFileCard state={spanishDPRState} />
          )}
          {attachment.downloadXmlUrl && (
            <DownloadEInvoiceXmlLink
              className="px-16 py-8"
              downloadXmlUrl={attachment.downloadXmlUrl}
            />
          )}
        </div>
      );
    });

    if (isTravelperkPayable(payable)) {
      previews.push(
        <AutomaticallyCollectedReceipt
          key={payableId}
          integrationId="travelperk"
          payableId={payableId}
        />,
      );
    }

    if (isUploadingDocument) {
      previews.push(
        <FileCard
          key="uploading"
          title={t('misc.uploadingFiles')}
          className="animate-pulse"
        />,
      );
    }

    return (
      <div>
        <div className="flex flex-col gap-8">
          <PanelPreviewsSection
            title={title}
            noPreviewContent={t('payables.panel.noReceipt')}
            previews={previews}
          />
          {canUploadDocumentaryEvidence && (
            <UploadDocumentaryEvidenceButton
              onDocumentUpload={onDocumentUpload}
              label={t('payables.panel.addAnotherReceipt')}
            />
          )}
        </div>
      </div>
    );
  }

  if (documentaryEvidence && !documentaryEvidence.validity.isValid) {
    const previews = attachments.map((attachment) => (
      <FileCard
        key={attachment.url}
        onClick={() => {
          onDocumentClick({ ...documentaryEvidence, ...attachment });
        }}
        title={getAttachmentPreviewText(attachment, t, localeFormat) as string}
        mimeType={attachment.mediaType}
      />
    ));

    if (isUploadingDocument) {
      previews.push(
        <FileCard
          key="uploading"
          title={t('misc.uploadingFiles')}
          className="animate-pulse"
        />,
      );
    }

    return (
      <div>
        <div className="flex flex-col gap-8">
          <PanelPreviewsSection
            title={title}
            noPreviewContent={t('payables.panel.noReceipt')}
            previews={previews}
          />
          <Callout
            variant="warning"
            title={t('payments.panel.invalidReceiptTitle')}
          >
            {documentaryEvidence.validity.reason && (
              <blockquote className="mb-16 border-0 border-s-2 border-solid border-border-warning pl-4 body-m">
                {documentaryEvidence.validity.reason}
              </blockquote>
            )}

            {canUploadDocumentaryEvidence && (
              <UploadDocumentaryEvidenceButton
                onDocumentUpload={onDocumentUpload}
                label={t('payables.panel.addAnotherReceipt')}
                variant="primaryWarning"
                hasNoHorizontalPadding={false}
                iconName={null}
              />
            )}
          </Callout>
        </div>
      </div>
    );
  }

  const previews = [];

  if (isUploadingDocument) {
    previews.push(
      <FileCard
        key="uploading"
        title={t('misc.uploadingFiles')}
        className="animate-pulse"
      />,
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-8">
        <PanelPreviewsSection
          title={title}
          noPreviewContent={t('payables.panel.noReceipt')}
          previews={previews}
        />
        {canUploadDocumentaryEvidence && (
          <UploadDocumentaryEvidenceButton
            onDocumentUpload={onDocumentUpload}
          />
        )}
      </div>
    </div>
  );
};

/**
 *  Helpers
 * */

const i18nKey = (key: I18nKey): string => key;

const User = ({
  id,
  name,
  children,
}: {
  id: string;
  name: string;
  children?: ReactNode;
}) => {
  return (
    <Tag variant="neutral">
      <UserAvatar name={name} id={id} size={16} /> {children}
    </Tag>
  );
};

export const getLocalizedMissingStatus = (user?: {
  id?: string;
  fullname: string;
}) => {
  const userName = user?.fullname ?? '';
  const userId = user?.id ?? '';

  return (
    <span className="flex items-center gap-4">
      <Trans
        i18nKey={i18nKey('documentaryEvidence.status.missing.other')}
        values={{ user: userName }}
        components={{
          user: <User id={userId} name={userName} />,
        }}
      />
    </span>
  );
};

const UploadDocumentaryEvidenceButton = ({
  onDocumentUpload,
  label,
  variant = 'tertiaryBrand',
  hasNoHorizontalPadding = true,
  iconName = 'circle-plus',
}: {
  label?: string;
  onDocumentUpload(files: File[] | null): void;
  iconName?: IconName | null | undefined;
} & Pick<UploadButtonProps, 'variant' | 'hasNoHorizontalPadding'>) => {
  const { t } = useTranslation('global');

  return (
    <UploadButton
      text={label ?? t('payables.panel.addReceipt')}
      variant={variant}
      hasNoHorizontalPadding={hasNoHorizontalPadding}
      fit="content"
      className="w-fit"
      iconName={iconName === null ? undefined : iconName}
      onUpload={(files) => onDocumentUpload(files ? Array.from(files) : null)}
    />
  );
};

const getAttachmentPreviewText = (
  attachment: {
    mediaType: string;
    downloadUrl: string;
    attachedAt: Date | undefined;
  },
  t: TGlobalFunctionTyped,
  localeFormat: LocaleFormat,
  isAffidavit?: boolean,
): ReactNode => {
  const fileType = getFileTypeFromMime(attachment.mediaType);

  const readableDate = t('payables.panel.uploadedOn', {
    date: attachment.attachedAt
      ? localeFormat(attachment.attachedAt, DATE_FORMAT.SHORT)
      : t('misc.unknown'),
  });

  if (!fileType) {
    return readableDate;
  }

  if (isAffidavit) {
    return (
      <span>
        {t('documentaryEvidence.missingDocumentaryEvidenceDeclaration')}
        <span className="mx-4 text-content-disabled">•</span>
        <span className="text-content-secondary-bg-primary">{readableDate}</span>
      </span>
    );
  }

  return (
    <span>
      {fileType}
      <span className="mx-4 text-content-disabled">•</span>
      <span className="text-content-secondary-bg-primary">{readableDate}</span>
    </span>
  );
};

export const useCanUploadDocumentaryEvidence = (
  payable: Payable | undefined,
) => {
  const isEditablePayable = useIsEditablePayable(payable);
  const company = useCompany();

  if (!payable) {
    return false;
  }

  const isAllowedPayableType = [
    'cardPurchase',
    'invoicePurchase',
    'claimedBill',
    'reversal',
  ].includes(payable.type ?? '');

  const isUkMileagePayable =
    payable.type === 'mileageAllowance' &&
    company?.mileage_scheme?.schemeType === 'UK';

  return isEditablePayable && (isAllowedPayableType || isUkMileagePayable);
};

export const useCanDeleteDocumentaryEvidence = (
  payable: Payable | undefined,
) => {
  const isEditablePayable = useIsEditablePayable(payable);

  const isUkMileagePayable = useIsUkMileagePayable(payable);

  if (!payable) {
    return false;
  }

  const hasMoreThanOneDocumentaryEvidence =
    (payable.documentaryEvidence?.attachments.length ?? 0) > 1;

  const isCardPayable = [
    'plasticCard',
    'subscriptionCard',
    'singleUseCard',
  ].includes(payable.subtype ?? '');

  return (
    isEditablePayable &&
    hasMoreThanOneDocumentaryEvidence &&
    (isCardPayable || isUkMileagePayable)
  );
};
